import { useEffect } from 'react';
import { initFirebase } from '../firebase';
import {
  identityApiRef,
  useApi,
  configApiRef,
} from '@backstage/core-plugin-api';
import { fcmApiRef } from '../api/FcmApi';
import { requestNotificationToken } from '../firebase/fcm';
import { useMe } from '../../../sev2/src/hooks/useMe';

export const useFirebaseNotifications = () => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const fcmApi = useApi(fcmApiRef);
  const { user, identity } = useMe();

  const userPHID = user?.phid;
  const userEntityRef = identity?.userEntityRef;

  const formatNotification = (
    data:
      | {
          action: string;
          task: string;
          fromColumnName?: string;
          columnName?: string;
          boardName?: string;
          actorEmail: string;
          assigneeEmail?: string;
        }
      | null
      | undefined,
  ) => {
    if (!data) return '';

    switch (data?.action) {
      case 'moved':
        return `${data.actorEmail} moved ${data.task} from ${data.fromColumnName} to ${data.columnName} on the ${data.boardName} board.`;
      case 'commented':
        return `${data.actorEmail} added a comment to ${data.task}.`;
      case 'reassigned':
        return `${data.actorEmail} reassigned ${data.task} `;
      default:
        return `${data.actorEmail} updated ${data.task}.`;
    }
  };

  useEffect(() => {
    if (userPHID && userEntityRef) {
      const messaging = initFirebase();
      if (!messaging) {
        console.error('Firebase messaging is not initialized.');
        return;
      }
      const vapidKey = configApi.getOptionalString(
        'integrations.firebase.vapidKey',
      );
      if (!vapidKey) {
        console.error('VAPID key is not set in the configuration.');
        return;
      }

      const requestPermissionAndToken = async () => {
        try {
          const token = await requestNotificationToken(vapidKey, messaging);

          if (token) {
            // Subscribe to the topic
            const response = await fcmApi.subscribeToTopic(token, userPHID);
            if (response.success === true) {
              console.info(response.message);
            } else {
              console.error('Error subscribing to topic:', response.message);
            }
          }
        } catch (err) {
          console.error('🚨 Error during FCM setup:', err);
        }
      };

      requestPermissionAndToken();

      messaging.onMessage(async payload => {
        const msg = payload?.data;
        const title = 'T' + msg?.taskID + ' ' + 'Update';

        const body = formatNotification(msg);

        fcmApi.sendNotification({
          title,
          body,
          recipients: userEntityRef ?? 'unknown',
        });
      });
    } else {
      console.error(
        '[requestPermissionAndToken] User PHID or Entity Ref is not available.',
      );
    }
  }, [identityApi, fcmApi, userPHID, userEntityRef]);
};
